import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react'
import { AiqIndividualReportView } from '@quebon/shared-learning'
import { aiqApi } from '@/api/aiq'

/**
 * Super Admin용 개인 AIQ 리포트 페이지.
 *
 * 학습현황 → 학습자별 → "AIQ 리포트" 버튼에서 진입.
 * Pre-AIQ만 완료된 경우에도 조회 가능.
 */
export function AiqLearnerReport() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const userId = Number(searchParams.get('userId'))
  const classProductId = searchParams.get('classProductId') ? Number(searchParams.get('classProductId')) : undefined
  const productId = searchParams.get('productId') ? Number(searchParams.get('productId')) : undefined
  const learnerName = searchParams.get('name') || '학습자'

  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ['aiqReport', 'admin', 'individual', userId, productId, classProductId],
    queryFn: async () => {
      const response = await aiqApi.getAdminIndividualReport(userId, productId, classProductId)
      return response.data.data
    },
    enabled: !!userId && (!!productId || !!classProductId),
  })

  if (!userId || (!productId && !classProductId)) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <p className="text-gray-800 font-medium mb-2">잘못된 접근</p>
          <p className="text-sm text-gray-500 mb-4">학습자 및 클래스 정보가 필요합니다.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            돌아가기
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-600">리포트를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (error || !reportData) {
    const errorMessage = error instanceof Error ? error.message : '리포트를 불러오는데 실패했습니다'
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-800 font-medium mb-2">리포트 조회 실패</p>
          <p className="text-sm text-gray-500 mb-4">{errorMessage}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            돌아가기
          </button>
        </div>
      </div>
    )
  }

  return (
    <AiqIndividualReportView
      data={reportData}
      productName={`${learnerName} AIQ 리포트`}
      backButton={
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          학습현황으로
        </button>
      }
    />
  )
}
